import { ModalAction } from '@overlayqa/shared/ui';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Layers01Icon, Sorting01Icon } from '@hugeicons/core-free-icons';
import { useA11yIgnored, useUnignoreA11y } from '../../../hooks/useA11yIgnored';
import { capture } from '../../../lib/analytics';
import { ToolbarSelect } from '../../shared/ToolbarSelect';

type ScopeFilter = 'all' | 'page' | 'project';
type SortKey = 'newest' | 'oldest' | 'selector' | 'wcag';

function pathFromUrl(url: string | null): string {
  if (!url) return '';
  try {
    return new URL(url).pathname || '/';
  } catch {
    return url;
  }
}

/**
 * Ignored accessibility violations, on the Audits tab of the Project Settings
 * modal. Moved from the retired /projects/:id/settings/accessibility page
 * (ProjectAccessibilitySettings.tsx) on 2026-09-17: the list, filters, sort and
 * un-ignore are unchanged; only the frame changed (no card, a heading above
 * and the count in the toolbar row, since the tab already groups it).
 */
export function IgnoredViolationsSection({ projectId }: { projectId: string }) {
  const { data, isLoading } = useA11yIgnored(projectId ?? '');
  const unignore = useUnignoreA11y(projectId ?? '');

  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');
  const [sort, setSort] = useState<SortKey>('newest');
  const [selected, setSelected] = useState<string[]>([]);

  const rows = useMemo(() => {
    if (!data) return [];
    const filtered =
      scopeFilter === 'all' ? data : data.filter((r) => r.scope === scopeFilter);
    const sorted = [...filtered];
    switch (sort) {
      case 'newest':
        sorted.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        break;
      case 'oldest':
        sorted.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
        break;
      case 'selector':
        sorted.sort((a, b) => a.selector.localeCompare(b.selector));
        break;
      case 'wcag':
        sorted.sort((a, b) => a.wcagCriterion.localeCompare(b.wcagCriterion));
        break;
    }
    return sorted;
  }, [data, scopeFilter, sort]);

  if (!projectId) return null;
  if (isLoading) return <div className="text-gray-500">Loading...</div>;
  if (!data || data.length === 0) {
    return (
      <section aria-labelledby="ignored-violations-heading">
        <h3 id="ignored-violations-heading" className="section-label mb-3">Ignored violations</h3>
        <p className="field-hint">
          No ignored violations. When you ignore violations during an audit, they'll appear here.
        </p>
      </section>
    );
  }

  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const unignoreSelected = () => {
    if (selected.length === 0) return;
    const selectedRows = (data ?? []).filter((r) => selected.includes(r.id));
    unignore.mutate(selected, {
      onSuccess: () => {
        for (const row of selectedRows) {
          capture('a11y_violation_unignored', {
            surface: 'dashboard',
            scope: row.scope,
            wcagCriterion: row.wcagCriterion,
            source: selected.length > 1 ? 'bulk' : 'per-row',
          });
        }
        setSelected([]);
      },
      onError: () => {
        // Selection is kept so the user can retry (2026-07-20 audit).
        toast.error("Couldn't un-ignore the selected violations. Please try again.");
      },
    });
  };

  return (
    <section aria-labelledby="ignored-violations-heading">
      <h3 id="ignored-violations-heading" className="section-label mb-3">Ignored violations</h3>
      {/* The count sits on its own line so an active "Scope: Page only" filter
          and the sort share one row inside the settings modal (Emily, 2026-10-12). */}
      <p className="field-hint mb-3">
        {data.length} violation{data.length === 1 ? '' : 's'} currently ignored
      </p>
      <div className="list-toolbar">
        <div className="list-toolbar__filters" role="search" aria-label="Filter ignored violations">
          <ToolbarSelect
            icon={Layers01Icon}
            value={scopeFilter}
            defaultValue="all"
            name="Scope"
            aria-label="Filter by scope"
            onChange={(v) => setScopeFilter(v as ScopeFilter)}
            options={[
              { value: 'all', label: 'All scopes' },
              { value: 'page', label: 'Page only' },
              { value: 'project', label: 'Project-wide only' },
            ]}
          />
          <ToolbarSelect
            icon={Sorting01Icon}
            value={sort}
            defaultValue="newest"
            aria-label="Sort violations"
            onChange={(v) => setSort(v as SortKey)}
            options={[
              { value: 'newest', label: 'Newest' },
              { value: 'oldest', label: 'Oldest' },
              { value: 'selector', label: 'Selector A-Z' },
              { value: 'wcag', label: 'WCAG criterion' },
            ]}
          />
        </div>
        <div className="list-toolbar__actions">
          <button
            type="button"
            className="btn-secondary btn-toolbar whitespace-nowrap"
            disabled={selected.length === 0 || unignore.isPending}
            onClick={unignoreSelected}
          ><ModalAction kind="retry">
            Un-ignore selected
          </ModalAction></button>
        </div>
      </div>
      <ul className="divide-y divide-gray-200">
        {rows.map((row) => (
          <li key={row.id} className="flex items-start gap-3 py-3">
            <input
              type="checkbox"
              checked={selected.includes(row.id)}
              onChange={() => toggle(row.id)}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="font-medium">{row.selector}</div>
              <div className="text-sm text-gray-600">
                WCAG {row.wcagCriterion} ·{' '}
                {row.scope === 'project'
                  ? 'Project-wide'
                  : `This page · ${pathFromUrl(row.pageUrl)}`}
                {row.createdByUserName ? ` · Ignored by ${row.createdByUserName}` : ''} ·{' '}
                {new Date(row.createdAt).toLocaleDateString()}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
